import React from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { t } from '../translations/translations';
import { getPostBySlug, formatPostDate } from '../data/insights';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

// Local block renderers — left-aligned headings, justified body paragraphs.
const Heading = ({ children }) => (
  <h2
    className="text-2xl md:text-3xl leading-snug text-gray-900 mt-12 mb-5"
    style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif' }}
  >
    {children}
  </h2>
);

const Paragraph = ({ children }) => (
  <p className="text-base leading-[1.85] text-gray-800" style={{ textAlign: 'justify' }}>
    {children}
  </p>
);

const Bullets = ({ items }) => (
  <ul className="list-disc pl-6 space-y-2 text-base leading-[1.85] text-gray-800 marker:text-gray-400">
    {items.map((it) => (
      <li key={it} style={{ textAlign: 'justify' }}>
        {it}
      </li>
    ))}
  </ul>
);

const ComparisonTable = ({ head, rows }) => (
  <div className="my-2 -mx-6 sm:mx-0 overflow-x-auto">
    <table className="w-full min-w-[560px] sm:min-w-0 border-collapse text-sm sm:text-base">
      <thead>
        <tr>
          {head.map((h, i) => (
            <th
              key={`${i}-${h}`}
              className="text-left align-bottom px-4 py-3 border-b border-gray-300"
              style={{
                fontFamily: 'Helvetica Neue, Arial, sans-serif',
                color: '#1a1a1a',
                fontWeight: 600,
                width: i === 0 ? '28%' : 'auto',
              }}
            >
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, ri) => (
          <tr key={`r-${ri}`} className={ri % 2 === 1 ? 'bg-gray-50' : ''}>
            {row.map((cell, ci) => (
              <td
                key={`r-${ri}-c-${ci}`}
                className="px-4 py-3 align-top border-b border-gray-200 text-gray-800 leading-relaxed"
                style={{
                  fontWeight: ci === 0 ? 600 : 400,
                  whiteSpace: 'normal',
                }}
              >
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const FaqList = ({ items }) => (
  <div className="space-y-7">
    {items.map((item) => (
      <div key={item.q}>
        <p className="text-lg leading-snug text-gray-900 mb-2" style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif' }}>
          {item.q}
        </p>
        <p className="text-base leading-[1.85] text-gray-800" style={{ textAlign: 'justify' }}>
          {item.a}
        </p>
      </div>
    ))}
  </div>
);

const renderBlock = (block, idx) => {
  switch (block.type) {
    case 'heading':
      return <Heading key={`b-${idx}`}>{block.text}</Heading>;
    case 'paragraph':
      return <Paragraph key={`b-${idx}`}>{block.text}</Paragraph>;
    case 'bullets':
      return <Bullets key={`b-${idx}`} items={block.items} />;
    case 'table':
      return <ComparisonTable key={`b-${idx}`} head={block.head} rows={block.rows} />;
    case 'faq':
      return <FaqList key={`b-${idx}`} items={block.items} />;
    default:
      return null;
  }
};

const renderLegacyBody = (body) =>
  body
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => <Paragraph key={`${p.length}-${p.slice(0, 24)}`}>{p}</Paragraph>);

const InsightsArticle = () => {
  const { slug } = useParams();
  const { language } = useLanguage();
  const tr = (key) => t(language, key);
  const post = getPostBySlug(slug);

  const seoTitle = post ? (post.seoTitle || `${post.title} | BIMCORP MX`) : '';
  const seoDescription = post ? (post.seoDescription || post.excerpt) : '';
  useDocumentMeta({ title: seoTitle, description: seoDescription });

  if (!post) {
    return <Navigate to="/insights" replace />;
  }

  const hasBlocks = Array.isArray(post.blocks) && post.blocks.length > 0;

  return (
    <div
      className="min-h-screen"
      style={{ backgroundColor: 'var(--pearl)', color: 'var(--dark-stone)' }}
      data-testid="insights-article-page"
    >
      <article className="max-w-3xl mx-auto px-6 sm:px-8 py-12 md:py-20">
        {/* Top breadcrumb */}
        <nav className="mb-12">
          <Link
            to="/insights"
            className="inline-flex items-center gap-1 text-xs uppercase tracking-[0.2em] text-gray-600 hover:text-blue-900 transition-colors"
            data-testid="insights-back-top"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            {tr('insights.backToInsights')}
          </Link>
        </nav>

        {/* Metadata */}
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs uppercase tracking-[0.2em] text-gray-500 mb-6">
          <span style={{ color: 'var(--light-blue)' }}>{post.category}</span>
          <span aria-hidden="true">·</span>
          <span>{post.author}</span>
          <span aria-hidden="true">·</span>
          <time dateTime={post.publishedAt}>
            {formatPostDate(post.publishedAt, language)}
          </time>
        </div>

        {/* Title */}
        <h1
          className="text-4xl sm:text-5xl leading-tight mb-10 text-gray-900"
          style={{ fontFamily: 'Helvetica Neue, Arial, sans-serif' }}
        >
          {post.title}
        </h1>

        {/* Excerpt as lede */}
        <p
          className="text-lg leading-relaxed text-gray-700 mb-10 italic"
          style={{ textAlign: 'justify' }}
        >
          {post.excerpt}
        </p>

        {/* Body */}
        <div className="space-y-6">
          {hasBlocks
            ? post.blocks.map((block, i) => renderBlock(block, i))
            : renderLegacyBody(post.body || '')}
        </div>

        {/* Bottom back link */}
        <div className="mt-16 pt-10 border-t border-gray-200">
          <Link
            to="/insights"
            className="inline-flex items-center gap-1 text-sm text-gray-700 hover:text-blue-900 transition-colors"
            data-testid="insights-back-bottom"
          >
            <ChevronLeft className="w-4 h-4" />
            {tr('insights.backToInsights')}
          </Link>
        </div>
      </article>
    </div>
  );
};

export default InsightsArticle;
